import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Workspace {
  id: string;
  name: string;
  created_by: string;
  role: string;
  created_at: string;
  is_superadmin?: boolean;
}

const STORAGE_KEY = "brickshare_active_workspace";
const WORKSPACE_EVENT = "brickshare-workspace-changed";

export function useWorkspace() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY)
  );
  const [loading, setLoading] = useState(true);

  const fetchWorkspaces = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setLoading(false); return; }

    const { data, error } = await supabase
      .from("workspace_members")
      .select("workspace_id, role, workspaces(id, name, created_by, created_at, is_superadmin)")
      .eq("user_id", session.user.id);

    if (error) {
      console.error("Error fetching workspaces:", error);
      setLoading(false);
      return;
    }

    const ws: Workspace[] = (data || []).map((m: any) => ({
      id: m.workspaces.id,
      name: m.workspaces.name,
      created_by: m.workspaces.created_by,
      role: m.role,
      created_at: m.workspaces.created_at,
      is_superadmin: Boolean(m.workspaces.is_superadmin),
    }));

    setWorkspaces(ws);

    // Auto-select if no active or invalid
    const storedId = localStorage.getItem(STORAGE_KEY);
    if (!storedId || !ws.find((w) => w.id === storedId)) {
      const first = ws[0];
      if (first) {
        localStorage.setItem(STORAGE_KEY, first.id);
        setActiveWorkspaceId(first.id);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  useEffect(() => {
    const syncActiveWorkspace = () => {
      setActiveWorkspaceId(localStorage.getItem(STORAGE_KEY));
    };

    window.addEventListener("storage", syncActiveWorkspace);
    window.addEventListener(WORKSPACE_EVENT, syncActiveWorkspace as EventListener);

    return () => {
      window.removeEventListener("storage", syncActiveWorkspace);
      window.removeEventListener(WORKSPACE_EVENT, syncActiveWorkspace as EventListener);
    };
  }, []);

  const switchWorkspace = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setActiveWorkspaceId(id);
    window.dispatchEvent(new Event(WORKSPACE_EVENT));
  }, []);

  const createWorkspace = useCallback(async (name: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    const { data: ws, error } = await supabase
      .from("workspaces")
      .insert({ name, created_by: session.user.id })
      .select("id")
      .single();

    if (error || !ws) {
      console.error("Error creating workspace:", error);
      return null;
    }

    await supabase.from("workspace_members").insert({
      workspace_id: ws.id,
      user_id: session.user.id,
      role: "owner",
    });

    await fetchWorkspaces();
    switchWorkspace(ws.id);
    return ws.id;
  }, [fetchWorkspaces, switchWorkspace]);

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || null;

  return {
    workspaces,
    activeWorkspace,
    activeWorkspaceId,
    loading,
    switchWorkspace,
    createWorkspace,
    refetch: fetchWorkspaces,
  };
}
